import React, { useEffect, useMemo, useState } from 'react';
import { currencySymbol } from '../../../utils/formatters';

const fmt = (n) => currencySymbol() + new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 }).format(Number(n || 0));

const ProgressCard = ({ label = 'Progress', percent = 0, amount, className = '' }) => {
  const clampedTarget = useMemo(() => {
    let p = Number(percent || 0);
    if (p < 0) p = 0;
    if (p > 100) p = 100;
    return p;
  }, [percent]);

  const [animatedPct, setAnimatedPct] = useState(0);
  const [animatedAmount, setAnimatedAmount] = useState(0);

  useEffect(() => {
    // Respect reduced motion
    const reduced = typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced) {
      setAnimatedPct(clampedTarget);
      setAnimatedAmount(Number(amount || 0));
      return;
    }

    // Animate percentage (bar width and number)
    const pctDurationMs = 800;
    const pctStart = performance.now();
    const pctFrom = 0;
    const pctTo = clampedTarget;
    let pctRaf;
    const pctStep = (t) => {
      const progress = Math.min(1, (t - pctStart) / pctDurationMs);
      const eased = 1 - Math.pow(1 - progress, 3); // easeOutCubic
      setAnimatedPct(pctFrom + (pctTo - pctFrom) * eased);
      if (progress < 1) pctRaf = requestAnimationFrame(pctStep);
    };
    pctRaf = requestAnimationFrame(pctStep);

    // Animate amount count-up
    const amt = Number(amount || 0);
    const amtDurationMs = 1000;
    const amtStart = performance.now();
    const amtFrom = 0;
    const amtTo = amt;
    let amtRaf;
    const amtStep = (t) => {
      const progress = Math.min(1, (t - amtStart) / amtDurationMs);
      const eased = 1 - Math.pow(1 - progress, 3);
      setAnimatedAmount(Math.round(amtFrom + (amtTo - amtFrom) * eased));
      if (progress < 1) amtRaf = requestAnimationFrame(amtStep);
    };
    amtRaf = requestAnimationFrame(amtStep);

    return () => {
      if (pctRaf) cancelAnimationFrame(pctRaf);
      if (amtRaf) cancelAnimationFrame(amtRaf);
    };
  }, [clampedTarget, amount]);

  return (
    <div className={`space-y-2 ${className}`}>
      <div className="text-lg font-semibold text-succession-foreground">{label}</div>
      {amount != null && (
        <div className="text-2xl md:text-3xl font-semibold text-succession-foreground">{fmt(animatedAmount)}</div>
      )}
      <div className="flex items-center gap-2 mt-1">
        <div className="flex-1 h-2 rounded bg-succession-secondary/10 overflow-hidden">
          <div className="h-full bg-succession-primary transition-all duration-700" style={{ width: `${animatedPct}%` }} />
        </div>
        <div className="text-succession-foreground font-semibold text-sm w-10 text-right">{Math.round(animatedPct)}%</div>
      </div>
    </div>
  );
};

export default ProgressCard;


